import { useSubscriptionBillingHistory } from '@forwardflow/hooks/customHooks'
import { Link } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import dayjs from 'dayjs'

export const PreviousBillings = () => {
   const [page, setPage] = useState(1)
   const [hasMore, setHasMore] = useState(true)
   const [billingHistory, setBillingHistory] = useState<any[]>([])
   const tableRef = useRef<HTMLDivElement>(null)
   const ITEMS_PER_PAGE = 10

   const { data, isLoading } = useSubscriptionBillingHistory({
      page,
      limit: ITEMS_PER_PAGE,
   })

   useEffect(() => {
      if (data?.data) {
         setBillingHistory((prev) => [...prev, ...data.data])
         if (data?.data?.length < ITEMS_PER_PAGE) {
            setHasMore(false)
         }
      }
   }, [data])

   const handleScroll = () => {
      if (tableRef.current && !isLoading && hasMore) {
         const { scrollTop, scrollHeight, clientHeight } = tableRef.current
         if (scrollTop + clientHeight >= scrollHeight - 20) {
            setPage((prev) => prev + 1)
         }
      }
   }

   useEffect(() => {
      const currentRef = tableRef.current
      if (currentRef) {
         currentRef.addEventListener('scroll', handleScroll)
         return () => currentRef.removeEventListener('scroll', handleScroll)
      }
   }, [isLoading, hasMore])

   const hasNoBillings = !isLoading && billingHistory.length === 0

   return (
      <div className="mt-8 pb-12">
         <div className="bg-[#474747]/10 rounded-xl border border-white/10 overflow-hidden">
            <div className="px-6 py-5 bg-white/[0.02]">
               <p className="text-white font-semibold text-base">Previous Billings</p>
            </div>
            <div className="border-t border-white/10" />

            <div ref={tableRef} className="max-h-[500px] overflow-y-auto">
               {/* Table Header */}
               <div className="grid grid-cols-[120px_1fr_120px_48px] gap-4 px-6 py-3 bg-white/[0.03] border-b border-white/10 sticky top-0 z-10">
                  <span className="text-white/40 text-xs font-medium uppercase tracking-wider">Date</span>
                  <span className="text-white/40 text-xs font-medium uppercase tracking-wider">Description</span>
                  <span className="text-white/40 text-xs font-medium uppercase tracking-wider">Amount</span>
                  <span />
               </div>

               {/* Table Rows */}
               {billingHistory.map((item, index) => (
                  <div
                     key={index}
                     className="grid grid-cols-[120px_1fr_120px_48px] gap-4 px-6 py-4 border-b border-white/[0.06] last:border-b-0 hover:bg-white/[0.02] transition-colors"
                  >
                     <span className="text-white font-medium text-sm">
                        {dayjs(item?.paidAt).format('MMM D')}
                     </span>
                     <span className="text-white/60 text-sm truncate">
                        {item?.description}
                     </span>
                     <span className="text-white font-medium text-sm">
                        ${item?.amountPaid?.toFixed(2)}
                     </span>
                     <Link
                        to={item?.invoicePdfUrl}
                        target="_blank"
                        className="flex items-center justify-center text-white/40 hover:text-white/70 transition-colors"
                     >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                           <path d="M8 3v7m0 0l3-3m-3 3L5 7m-3 5v1a2 2 0 002 2h8a2 2 0 002-2v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                     </Link>
                  </div>
               ))}

               {hasNoBillings && (
                  <div className="py-12 text-center text-white/40 text-sm">
                     No billing history available
                  </div>
               )}

               {isLoading && (
                  <div className="px-6 py-2 space-y-1">
                     {[...Array(5)].map((_, i) => (
                        <div
                           key={i}
                           className="grid grid-cols-[120px_1fr_120px_48px] gap-4 py-4"
                        >
                           <div className="h-4 w-16 bg-white/[0.06] rounded animate-pulse" />
                           <div className="h-4 w-3/4 bg-white/[0.06] rounded animate-pulse" />
                           <div className="h-4 w-16 bg-white/[0.06] rounded animate-pulse" />
                           <div className="h-4 w-4 bg-white/[0.06] rounded animate-pulse mx-auto" />
                        </div>
                     ))}
                  </div>
               )}
            </div>
         </div>
      </div>
   )
}
